import React, { FC, UIEvent, useRef } from 'react';
import { useRouter } from 'next/router';

import { AppLink } from 'types/AppLink';
import { normalizeTrailingSlash } from 'utils/links';
import { SubNavigationLink } from 'components/molecules/SubNavigationLink';
import { PageNavigationLink } from 'navigation/types';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';

interface ScrollPositionStore {
  path: string | null;
  left: number;
}

const scrollPosition: ScrollPositionStore = {
  path: null,
  left: 0,
};

interface Props {
  currentLink: AppLink;
  links: PageNavigationLink[];
}

export const SubNavigation: FC<Props> = ({ currentLink, links }) => {
  const { asPath } = useRouter();
  const isPathCurrentLinkExact =
    normalizeTrailingSlash(asPath) === normalizeTrailingSlash(currentLink.href);

  const scrollRef = useRef<HTMLUListElement>(null);

  const handleScroll = (event: UIEvent<HTMLUListElement>) => {
    scrollPosition.path = currentLink.href;
    scrollPosition.left = event.currentTarget.scrollLeft;
  };

  useIsomorphicLayoutEffect(() => {
    const restoredPosition =
      scrollPosition.path === currentLink.href ? scrollPosition.left : null;

    if (scrollRef.current && restoredPosition !== null) {
      scrollRef.current.scrollTo(restoredPosition, 0);
    }
  }, [currentLink]);

  return (
    <div className="pb-8 mb-10 tablet:mb-16">
      <nav className="m-auto max-w-page-content">
        <ul
          ref={scrollRef}
          className="flex py-1 -my-1 overflow-x-scroll snap-x __header:place-content-center __header:overflow-x-auto scrollbar-hide"
          onScroll={handleScroll}
        >
          <SubNavigationLink
            className="pl-6 __header:hidden"
            link={currentLink}
            isActive={isPathCurrentLinkExact}
          />
          {links.map((link) => (
            <SubNavigationLink
              key={link.href}
              link={link}
              isActive={normalizeTrailingSlash(asPath).startsWith(
                normalizeTrailingSlash(link.matchHref ?? link.href)
              )}
            />
          ))}
        </ul>
      </nav>
    </div>
  );
};
